import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { format, parseISO } from 'date-fns';
import { Loader2, Download } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const cents = (n) => `$${((n || 0) / 100).toFixed(2)}`;
const safeParse = (s) => { try { return parseISO(s); } catch { return null; } };

export default function PublicInvoicePage() {
  const { token } = useParams();
  const [inv, setInv] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [downloading, setDownloading] = useState(false);

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const res = await axios.get(`${API_URL}/api/public/invoices/${token}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Invoice-${inv?.invoiceNumber || 'ranchworks'}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Could not generate PDF');
    } finally {
      setDownloading(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/public/invoices/${token}`);
        setInv(data);
      } catch (e) {
        setErr(e.response?.data?.detail || 'Invoice not found');
      } finally { setLoading(false); }
    })();
  }, [token]);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin" /></div>
  );
  if (err) return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <div className="rounded border p-6 max-w-md text-center" data-testid="public-invoice-not-found">
        <p className="text-lg font-semibold">Invoice not found</p>
        <p className="text-sm text-muted-foreground mt-1">{err}</p>
      </div>
    </div>
  );

  const logoUrl = inv.businessLogoPath
    ? `${API_URL}/api/files/logos/${inv.businessLogoPath.replace(/^ranchworks\/logos\//, '')}`
    : null;
  const enabledOptions = (inv.paymentOptions || []).filter((p) => p.enabled);

  return (
    <div className="min-h-screen bg-muted/30 py-4 sm:py-10 px-3" data-testid="public-invoice-page">
      <div className="max-w-2xl mx-auto space-y-3">
        <div className="flex justify-end">
          <Button onClick={downloadPdf} disabled={downloading} variant="outline" size="sm" data-testid="public-download-pdf-btn">
            {downloading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Download className="h-4 w-4 mr-1" />}
            Download PDF
          </Button>
        </div>
        <div className="bg-white rounded shadow-sm p-4 sm:p-8 space-y-5">
        <div className="flex items-start justify-between gap-3">
          <div>
            {logoUrl ? (
              <img src={logoUrl} alt="Logo" className="h-12 mb-2 object-contain" data-testid="public-logo" />
            ) : null}
            <p className="font-bold text-lg" data-testid="public-business-name">{inv.businessName || 'RanchWorks Provider'}</p>
            {inv.providerName && <p className="text-xs text-muted-foreground">{inv.providerName}</p>}
            {inv.businessAddress && <p className="text-xs">{inv.businessAddress}</p>}
            {inv.businessPhone && <p className="text-xs">{inv.businessPhone}</p>}
            {inv.businessEmail && <p className="text-xs">{inv.businessEmail}</p>}
          </div>
          <div className="text-right">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Invoice</p>
            <p className="font-mono font-bold text-lg" data-testid="public-invoice-number">{inv.invoiceNumber}</p>
            <p className="text-xs mt-1">Date: {inv.invoiceDate}</p>
            {inv.dueDate && <p className="text-xs">Due: {inv.dueDate}</p>}
            <p className="text-xs mt-1 font-semibold" data-testid="public-invoice-status">{inv.status}</p>
          </div>
        </div>

        <div className="text-sm border-t pt-3">
          <p className="text-xs uppercase text-muted-foreground">Bill to</p>
          <p className="font-medium" data-testid="public-client-name">{inv.clientName}</p>
          {inv.jobServiceName && (
            <p className="text-xs text-muted-foreground">
              {inv.jobServiceName}
              {inv.jobAnimalCount ? ` · ${inv.jobAnimalCount} animal${inv.jobAnimalCount === 1 ? '' : 's'}` : ''}
              {inv.jobServiceDate ? ` · ${format(safeParse(inv.jobServiceDate) || new Date(), 'MMM d, yyyy')}` : ''}
            </p>
          )}
          {inv.jobLocation && <p className="text-xs">{inv.jobLocation}</p>}
        </div>

        <table className="w-full text-sm" data-testid="public-line-items">
          <thead className="border-b text-left">
            <tr><th className="py-1">Description</th><th className="text-right">Qty</th><th className="text-right">Unit</th><th className="text-right">Total</th></tr>
          </thead>
          <tbody>
            {(inv.lineItems || []).map((li) => (
              <tr key={li.id} className="border-b">
                <td className="py-1">{li.description}</td>
                <td className="text-right">{li.quantity}</td>
                <td className="text-right tabular-nums">{cents(li.unitPriceCents)}</td>
                <td className="text-right tabular-nums">{cents(li.totalCents)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="text-right text-sm space-y-0.5">
          <p>Subtotal <span className="tabular-nums ml-2">{cents(inv.subtotalCents)}</span></p>
          {(inv.discountCents || 0) > 0 && <p>Discount <span className="text-red-700 tabular-nums ml-2">-{cents(inv.discountCents)}</span></p>}
          {(inv.taxCents || 0) > 0 && <p>Tax <span className="tabular-nums ml-2">+{cents(inv.taxCents)}</span></p>}
          <p className="font-bold text-lg">Total <span className="tabular-nums ml-2" data-testid="public-invoice-total">{cents(inv.totalCents)}</span></p>
          {(inv.amountPaidCents || 0) > 0 && (
            <p className="text-green-700 text-sm">Paid <span className="tabular-nums ml-2">{cents(inv.amountPaidCents)}</span></p>
          )}
        </div>

        {inv.invoiceNote && (
          <p className="text-sm italic border-l-2 pl-2 text-muted-foreground" data-testid="public-invoice-note">{inv.invoiceNote}</p>
        )}

        {enabledOptions.length > 0 && (
          <div className="rounded border bg-muted/40 p-3 sm:p-4 space-y-2" data-testid="public-payment-options">
            <p className="text-xs font-semibold uppercase">How to pay</p>
            {enabledOptions.map((p) => (
              <div key={p.id} className="text-sm" data-testid={`public-payment-option-${p.id}`}>
                <p className="font-medium">{p.label}</p>
                {p.value && /^https?:\/\//i.test(p.value) ? (
                  <a href={p.value} target="_blank" rel="noopener noreferrer" className="text-xs underline break-all">{p.value}</a>
                ) : p.value ? (
                  <p className="text-xs">{p.value}</p>
                ) : null}
                {p.instructions && <p className="text-xs whitespace-pre-line">{p.instructions}</p>}
              </div>
            ))}
          </div>
        )}

        <p className="text-[10px] text-center text-muted-foreground pt-2">
          Powered by RanchWorks
        </p>
        </div>
      </div>
    </div>
  );
}
